import { api } from "./client";
import type { Course, CourseStudent, EnrolledCourse, LibraryCourse } from "@/types";

// ── Bibliothèque inter-profs ────────────────────────────────────────────────────

export const listAllCourses = () =>
  api.get<LibraryCourse[]>("/api/instructor/courses/all");

export interface CoursePreview {
  id: number;
  title: string;
  color: string;
  teacher: string | null;
  exercises: { id: number; title: string; published: boolean; step_count: number }[];
}

export const previewCourse = (id: number) =>
  api.get<CoursePreview>(`/api/instructor/courses/${id}/preview`);

export const copyCourseToMine = (id: number) =>
  api.post<Course>(`/api/instructor/courses/${id}/copy-to-mine`, {});

// ── Côté élève ────────────────────────────────────────────────────────────────

export const listEnrolledCourses = () =>
  api.get<EnrolledCourse[]>("/api/courses");

export const joinCourse = (code: string) =>
  api.post<{ id: number; title: string; color: string }>("/api/courses/join", { code });

// ── Mes cours (enseignant) ──────────────────────────────────────────────────────

export const listMyCourses = () =>
  api.get<Course[]>("/api/instructor/courses");

export const createCourse = (title: string, color?: string) =>
  api.post<Course>("/api/instructor/courses", { title, color });

export const updateCourse = (
  id: number,
  data: { title?: string; color?: string; archived?: boolean },
) => api.patch<Course>(`/api/instructor/courses/${id}`, data);

export const duplicateCourse = (id: number, title?: string) =>
  api.post<Course>(`/api/instructor/courses/${id}/duplicate`, { title });

// Suppression définitive (réservée aux cours archivés, garde-fou côté API).
export const deleteCourse = (id: number) =>
  api.delete<{ ok: boolean }>(`/api/instructor/courses/${id}`);

export const regenerateCode = (id: number) =>
  api.post<{ join_code: string }>(`/api/instructor/courses/${id}/regenerate-code`);

// ── Roster ──────────────────────────────────────────────────────────────────────

export const listStudents = (courseId: number) =>
  api.get<CourseStudent[]>(`/api/instructor/courses/${courseId}/students`);

export const addStudent = (courseId: number, identifier: string) =>
  api.post<CourseStudent>(`/api/instructor/courses/${courseId}/students`, { identifier });

export const removeStudent = (courseId: number, studentId: number) =>
  api.delete<{ ok: boolean }>(`/api/instructor/courses/${courseId}/students/${studentId}`);

export const removeStudentsBulk = (courseId: number, ids: number[]) =>
  api.post<{ ok: boolean; removed: number }>(
    `/api/instructor/courses/${courseId}/students/remove`, { ids },
  );

// ── Classes assignées au cours ───────────────────────────────────────────────

export interface CourseClassOption { id: number; name: string; assigned: boolean; }

export const listCourseClasses = (courseId: number) =>
  api.get<CourseClassOption[]>(`/api/instructor/courses/${courseId}/classes`);

export const assignClass = (courseId: number, classId: number) =>
  api.post<{ ok: boolean }>(`/api/instructor/courses/${courseId}/classes`, { class_id: classId });

export const unassignClass = (courseId: number, classId: number) =>
  api.delete<{ ok: boolean }>(`/api/instructor/courses/${courseId}/classes/${classId}`);
